import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from 'utils/api';
import { replaceNonNumber, replaceSpecialCharacter } from 'tools/StringTool';
import Layout from 'layout';
import StyledActiveButton from 'components/Button/StyledActiveButton';
import MallSecondAuthContent from './MallSecondAuthContent';
import { getMallSecondAuthType, MALL_SECOND_AUTH_TYPE } from './constants';
import {
  ContentSectionContent,
  ContentSectionContentWrapper,
  ContentSectionTitle,
  ContentSectionWrapper,
  ContentWrapper,
  TitleText,
  TitleWrapper,
  Wrapper,
} from './style';
import CommonModal from '../../components/Modal/CommonModal';
import { requestApi } from '../../utils/request';
import dayjs from 'dayjs';
import useCountDown from '../../hooks/useCountDown';
import { COMMON_ERR_MESSAGE } from '../../constants/message';
import { useUI } from '../../contexts/UIContext';

const MallSecondAuthPage = () => {
  const { setContextLoading } = useUI();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [secondAuthDetail, setSecondAuthDetail] = useState({
    secondAuthHash: '',
    businessDataProviderMallMappingSeq: 0,
    businessRegistrationNumber: '',
    mallSeq: 0,
    mallName: '',
    secondAuthTypeCd: '',
    regDt: '',
    secondAuthDetail: '',
    secondAuthQuestion: '',
  });
  const [mallSecondAuthValue, setMallSecondAuthValue] = useState('');
  const [isDisabledInput, setIsDisabledInput] = useState(true);
  // 모달
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({});
  // api 통신 로딩
  const [isLoading, setIsLoading] = useState(false);

  const hash = searchParams.get('hash')?.trim();
  const {
    businessRegistrationNumber,
    regDt,
    secondAuthTypeCd,
    secondAuthDetail: contactInfo,
  } = secondAuthDetail;
  const startRemainTime = dayjs(regDt)
    .add(3, 'minute')
    .diff(dayjs(), 'seconds');
  const { remainingTime } = useCountDown(dayjs(regDt).add(3, 'minute'));
  const isGeneralSecondAuthType =
    MALL_SECOND_AUTH_TYPE[secondAuthTypeCd]?.requirement === 'CODE';

  // 2차인증 값 입력
  const onChangeTextField = event => {
    const value =
      event.target.name === 'OTP' || event.target.name === 'PHONE_NUMBER'
        ? replaceNonNumber(event.target.value)
        : replaceSpecialCharacter(event.target.value.trim());
    setMallSecondAuthValue(value);
  };

  // 2차 인증 버튼 비활성화
  const checkIsDisabledBtn = () => {
    return !mallSecondAuthValue || !(remainingTime > 0);
  };

  // 2차인증 완료 버튼 클릭
  const onClickSendBtn = () => {
    setIsDisabledInput(true);
    fetchSecondAuth();
  };

  // 인증 시간 만료(10000) 에러 케이스 모달
  const showExpiredTimeModal = (message, data) => {
    setShowModal(true);
    setModalData({
      contents: message,
      onClickConfirmBtn: async () => {
        await fetchScraping({
          successFnc: () => {
            navigate('/', { replace: true });
          },
          seq: data.businessDataProviderMallMappingSeq,
        });
      },
      confirmBtnName: '재요청',
      onClickCancelBtn: () => {
        navigate('/', { replace: true });
      },
      cancelBtnName: '취소',
    });
  };

  // 인증 시간 만료 외 에러 케이스 모달 (원버튼)
  const showErrModal = message => {
    setShowModal(true);
    setModalData({
      contents: message,
      confirmBtnName: '확인',
      onClickConfirmBtn: () => navigate('/', { replace: true }),
    });
  };

  // 해시키로 정보 조회
  const fetchSecondAuthInfo = async ({ secondAuthHash }) => {
    try {
      const res = await requestApi({
        url: `/v1/au/second-auth?secondAuthHash=${secondAuthHash}`,
        method: 'GET',
      });

      if (res.success) {
        setSecondAuthDetail(res.data.result?.secondAuthDetail);
        setIsDisabledInput(false);
      } else {
        if (res['error-no'] === 10000) {
          showExpiredTimeModal(res.message, res.data);
        } else {
          showErrModal(res.message, () => navigate('/', { replace: true }));
        }
      }
    } catch (error) {
      setShowModal(true);
      setModalData({
        contents: error.response.data.message,
        confirmBtnName: '확인',
        onClickConfirmBtn: () => navigate('/', { replace: true }),
      });
    } finally {
      setContextLoading(false);
    }
  };

  // 2차인증 값 전송 api
  const fetchSecondAuth = () => {
    setIsLoading(true);
    const data = {
      secondAuthHash: hash,
      authValue: mallSecondAuthValue,
    };
    api({
      url: '/v1/au/second-auth',
      method: 'post',
      headers: { 'content-type': 'application/json' },
      data: data,
    })
      .then(res => {
        const { success, message, 'error-no': errorNo } = res.data;
        if (success) {
          setShowModal(true);
          setModalData({
            contents: message,
            confirmBtnName: '데이터 연동 관리 가기',
            onClickConfirmBtn: () =>
              navigate('/data-linkage/manage', { replace: true }),
          });
        } else {
          // 10000: 인증 시간 만료
          if (errorNo === 10000) {
            showExpiredTimeModal(message, secondAuthDetail);
          } else {
            setIsDisabledInput(false);
            showErrModal(message, () => setShowModal(false));
          }
        }
      })
      .catch(err => {
        console.error(err);
        setIsDisabledInput(false);
        const { data } = err.response;
        setShowModal(true);
        setModalData({
          contents: data.message,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // 스크래핑 재요청 api
  const fetchScraping = async ({ successFnc, seq }) => {
    try {
      const res = await requestApi({
        url: `/v1/au/data-provider-mall/requestScraping/${seq}`,
        method: 'POST',
      });
      if (res.success) {
        successFnc();
      } else {
        setShowModal(true);
        setModalData({
          contents: res.message,
        });
      }
    } catch (error) {
      setShowModal(true);
      setModalData({
        contents: COMMON_ERR_MESSAGE,
      });
      console.error(error);
    }
  };

  useEffect(() => {
    if (hash) {
      (async () => {
        await fetchSecondAuthInfo({ secondAuthHash: hash });
      })();
    } else {
      setShowModal(true);
      setModalData({
        contents:
          '올바른 경로가 아닙니다.\n문자로 전달 받은 링크를 통해 다시 한번 접속해 주세요.',
        confirmBtnName: '확인',
        onClickConfirmBtn: () => navigate('/', { replace: true }),
      });
    }
  }, [hash]);

  // 브라우저 인증: 자동으로 값(CONFIRM) 입력
  useEffect(() => {
    if (
      !!secondAuthTypeCd &&
      MALL_SECOND_AUTH_TYPE[secondAuthTypeCd]?.requirement === 'BROWSER'
    ) {
      setMallSecondAuthValue('CONFIRM');
    }
  }, [secondAuthTypeCd]);

  if (!businessRegistrationNumber) {
    return (
      <Layout pageTitle="2차 인증 진행">
        <CommonModal
          modalData={modalData}
          showModal={showModal}
          setShowModal={setShowModal}
        />
      </Layout>
    );
  }

  return (
    <Layout pageTitle="2차 인증 진행">
      <Wrapper>
        <TitleWrapper>
          <TitleText>
            {isGeneralSecondAuthType === true
              ? '연동 판매몰의 2차 인증을\n진행해 주세요.'
              : '연동 판매몰의 보안 인증을\n진행해 주세요.'}
          </TitleText>
        </TitleWrapper>
        <ContentWrapper>
          <ContentSectionWrapper>
            <ContentSectionTitle>사업자 번호</ContentSectionTitle>
            <ContentSectionContentWrapper>
              <ContentSectionContent>
                {businessRegistrationNumber}
              </ContentSectionContent>
            </ContentSectionContentWrapper>
          </ContentSectionWrapper>
          {/* 2차인증 컨텐츠 */}
          {MALL_SECOND_AUTH_TYPE[secondAuthTypeCd]?.code && (
            <MallSecondAuthContent
              isGeneralSecondAuthType={isGeneralSecondAuthType}
              mallSecondAuthObj={getMallSecondAuthType(
                secondAuthTypeCd,
                contactInfo
              )}
              secondAuthDetail={secondAuthDetail}
              mallSecondAuthValue={mallSecondAuthValue}
              onChangeTextField={onChangeTextField}
              isDisabledInput={isDisabledInput}
              fetchScraping={fetchScraping}
              startRemainTime={startRemainTime}
              remainingTime={remainingTime}
              setModalData={setModalData}
              setShowModal={setShowModal}
            />
          )}
          <StyledActiveButton
            onClick={onClickSendBtn}
            margin="63px 0 0"
            disabled={checkIsDisabledBtn() || isLoading}
          >
            {isGeneralSecondAuthType === true
              ? '2차 인증 완료'
              : '보안 인증 완료'}
          </StyledActiveButton>
        </ContentWrapper>
      </Wrapper>
      <CommonModal
        modalData={modalData}
        showModal={showModal}
        setShowModal={setShowModal}
      />
    </Layout>
  );
};

export default MallSecondAuthPage;
